/**
 * NCC: soft image swap on product cards (Lager-style crossfade).
 *
 * Horizon's product card previews the second image on hover by jumping its nested slideshow to another
 * slide with `animate: false` - an instant cut. This wraps `previewImage` / `resetImage` on the
 * `product-card` element: before the jump, the slide that is currently showing is cloned into an
 * overlay that sits on top of the gallery, the jump happens underneath, and the clone fades out
 * over `--ncc-fade-duration` (assets/ncc-theme.css). The slideshow itself is untouched, so variant
 * swatches, card arrows and the card carousel keep working exactly as before.
 *
 * The slide that is cloned is the one actually in view (read from the scroller's position, never from
 * the slideshow's `current` index, which can lag behind a scroll). The jump runs first and the clone is
 * only made when it moved the gallery: when the card already shows the target (a repeated pointerenter,
 * a leave from a card that was never previewed) nothing is cloned and a fade still running is left alone.
 *
 * The clone keeps the slide's classes (so a "fitted" cut-out keeps its padding), its images are pinned to the
 * rendition the slide is showing (see below), it is marked inert and aria-hidden, and any clone still fading
 * when the next swap starts is dropped first.
 */
const FALLBACK_MS = 400;

customElements.whenDefined('product-card').then((ProductCard) => {
  const proto = ProductCard.prototype;
  const { previewImage, resetImage } = proto;
  if (typeof previewImage !== 'function' || typeof resetImage !== 'function') return;

  /**
   * The slide the scroller is showing: the one whose start edge sits closest to the scroll position.
   * @param {any} slideshow
   * @returns {HTMLElement | undefined}
   */
  const shownSlide = (slideshow) => {
    const slides = /** @type {HTMLElement[] | undefined} */ (slideshow?.slides);
    const scroller = /** @type {HTMLElement | undefined} */ (slideshow?.refs?.scroller);
    if (!slides?.length) return undefined;
    if (!(scroller instanceof HTMLElement)) return slides[slideshow?.current ?? 0];

    const position = scroller.scrollLeft;
    let best = slides[0];
    let bestDistance = Infinity;
    for (const slide of slides) {
      const distance = Math.abs(slide.offsetLeft - position);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slide;
      }
    }
    return best;
  };

  /**
   * Snapshot the visible slide over the gallery before `swap` cuts to another one, then fade it out.
   * @param {HTMLElement} card
   * @param {(...args: any[]) => any} swap
   * @param {any[]} args
   */
  const crossfade = (card, swap, args) => {
    const [event] = args;
    if (event?.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return swap.apply(card, args);
    }

    const slideshow = /** @type {HTMLElement | undefined} */ (card.refs?.slideshow);
    const slides = /** @type {HTMLElement[] | undefined} */ (/** @type {any} */ (slideshow)?.slides);
    const before = shownSlide(slideshow);

    // the jump is instant and synchronous, so the gallery already shows the target when we get back
    const result = swap.apply(card, args);
    if (!slideshow || !(before instanceof HTMLElement) || shownSlide(slideshow) === before) return result;

    slideshow.querySelector(':scope > .ncc-card-fade')?.remove();

    const clone = /** @type {HTMLElement} */ (before.cloneNode(true));
    clone.className = `${before.className} ncc-card-fade`;
    if (before === slides?.[0]) clone.classList.add('ncc-card-fade--first');
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    for (const el of clone.querySelectorAll('[ref], [id]')) {
      el.removeAttribute('ref');
      el.removeAttribute('id');
    }
    // The clone must paint the very bitmap the slide shows. Horizon's lazy card images carry `sizes="auto, ..."`,
    // which the browser only honours on a lazy image; a fresh clone re-runs source selection, and any change (a
    // `loading` swap, a lazy image not yet in view) picks another rendition and fetches it - the clone then paints
    // blank until it lands, so the underlying image shows through and the swap reads as a double flip. Pinning the
    // original's currentSrc (no srcset / sizes) makes the clone a cache hit that paints on its first frame.
    const originals = before.querySelectorAll('img');
    clone.querySelectorAll('img').forEach((img, index) => {
      const source = originals[index]?.currentSrc;
      if (source) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = source;
      }
      img.loading = 'eager';
    });

    slideshow.append(clone);

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clone.remove();
    };
    clone.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, FALLBACK_MS);
    // commit the start state, then let the transition run
    requestAnimationFrame(() => requestAnimationFrame(() => clone.setAttribute('data-fading', '')));

    return result;
  };

  proto.previewImage = function (...args) {
    return crossfade(this, previewImage, args);
  };

  proto.resetImage = function (...args) {
    return crossfade(this, resetImage, args);
  };
});
